// Timelines: everything MailSmith knows about one order, customer, or other
// entity, found by a key value. Components go in src/features/timelines.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { History, RotateCw, Search, SearchX, X } from 'lucide-react'
import { useMemo, useState, type FormEvent } from 'react'
import type { Timeline } from '@/api/types'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState } from '@/components/QueryState'
import { Button, IconButton } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Segmented } from '@/components/ui/segmented'
import { SkeletonText } from '@/components/ui/skeleton'
import { timelineQuery } from '@/features/timelines/api'
import {
  buildEntries,
  filterByMode,
  openItems,
  summarize,
  type ModeFilter,
} from '@/features/timelines/model'
import { PendingCard, TimelineStream } from '@/features/timelines/TimelineView'
import { useAppScope } from '@/shell/useAppScope'

interface TimelineSearch {
  key?: string
  mode?: 'live' | 'test'
}

export const Route = createFileRoute('/timelines')({
  staticData: { crumb: 'Timelines' },
  validateSearch: (s: Record<string, unknown>): TimelineSearch => ({
    key: typeof s.key === 'string' && s.key.trim() ? s.key.trim() : undefined,
    mode: s.mode === 'live' || s.mode === 'test' ? s.mode : undefined,
  }),
  component: TimelinesPage,
})

const MODES = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'test', label: 'Test' },
] as const

function TimelinesPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { appId, application } = useAppScope()
  const key = search.key ?? ''
  const q = useQuery(timelineQuery(appId, key))

  const setSearch = (patch: Partial<TimelineSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }) })

  return (
    <>
      <PageHeader
        title="Timelines"
        description={`Everything MailSmith knows about one order, customer, or other entity${
          application ? ` in ${application.name}` : ''
        }: its events, what each rule decided, emails waiting to send, and the messages that went out.`}
        actions={
          key ? (
            <IconButton
              label="Refresh"
              variant="secondary"
              onClick={() => void q.refetch()}
              disabled={q.isFetching}
            >
              <RotateCw className={q.isFetching ? 'animate-spin' : undefined} />
            </IconButton>
          ) : undefined
        }
      />
      <KeySearch
        // Remount when the URL changes elsewhere (a key chip, back button).
        key={key}
        initial={key}
        onSubmit={(k) => setSearch({ key: k || undefined, mode: undefined })}
      />
      <div className="mt-5">
        {!key ? (
          <Card>
            <EmptyState
              icon={History}
              title="Look up an order or other entity"
              description={
                <>
                  Enter a key value, such as an order number, to see its events, the decisions
                  MailSmith made, and its emails. To search one key, use{' '}
                  <code className="font-mono text-xs">name=value</code>, for example{' '}
                  <code className="font-mono text-xs">order=WS00422264</code>. Event types declare
                  their keys on the Settings tab.
                </>
              }
              action={
                <Button asChild variant="secondary">
                  <Link to="/event-types">Go to event types</Link>
                </Button>
              }
            />
          </Card>
        ) : q.isPending ? (
          <Card className="p-4" aria-busy>
            <SkeletonText lines={8} />
          </Card>
        ) : q.error ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <Results
            timeline={q.data}
            mode={search.mode}
            onMode={(mode) => setSearch({ mode })}
            keyValue={key}
            scoped={!!appId}
          />
        )}
      </div>
    </>
  )
}

function KeySearch({ initial, onSubmit }: { initial: string; onSubmit: (key: string) => void }) {
  const [text, setText] = useState(initial)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(text.trim())
  }
  return (
    <form role="search" onSubmit={submit} className="flex max-w-2xl gap-2">
      <Input
        autoFocus={!initial}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Order number or other key value"
        aria-label="Key value"
        spellCheck={false}
        className="font-mono"
        leading={<Search className="size-4" />}
        trailing={
          text ? (
            <IconButton
              label="Clear"
              size="xs"
              type="button"
              onClick={() => {
                setText('')
                onSubmit('')
              }}
            >
              <X />
            </IconButton>
          ) : undefined
        }
      />
      <Button type="submit" variant="primary">
        Find
      </Button>
    </form>
  )
}

function Results({
  timeline,
  mode,
  onMode,
  keyValue,
  scoped,
}: {
  timeline: Timeline
  mode: ModeFilter
  onMode: (m: ModeFilter) => void
  keyValue: string
  scoped: boolean
}) {
  const all = summarize(timeline)
  const shown = useMemo(() => filterByMode(timeline, mode), [timeline, mode])
  const entries = useMemo(() => buildEntries(shown), [shown])
  const open = useMemo(() => openItems(shown.pending), [shown.pending])
  const nothing =
    timeline.events.length === 0 && timeline.messages.length === 0 && timeline.pending.length === 0

  if (nothing)
    return (
      <Card>
        <EmptyState
          icon={SearchX}
          title={`Nothing found for “${keyValue}”`}
          description={
            timeline.matched_keys.length === 0
              ? 'No event type declares a key yet, so there’s no history to search. Add keys to an event type on its Settings tab.'
              : `Searched ${timeline.matched_keys.map((k) => k.split('=')[0]).join(', ')}${
                  scoped ? ' in this application' : ''
                }. Key values match exactly, including case. History is kept for the application’s retention period.`
          }
        />
      </Card>
    )

  const s = summarize(shown)
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-2">
          <span className="text-ink">{s.events}</span> {s.events === 1 ? 'event' : 'events'} ·{' '}
          <span className="text-ink">{s.messages}</span> {s.messages === 1 ? 'message' : 'messages'}
          {s.open > 0 && (
            <>
              {' '}
              · <span className="text-ink">{s.open}</span> waiting to send
            </>
          )}
          <span className="ml-2 font-mono text-xs text-ink-3">
            {timeline.matched_keys.length <= 4
              ? timeline.matched_keys.join(', ')
              : `${timeline.matched_keys.length} keys`}
          </span>
        </p>
        {all.test && all.live && (
          <Segmented
            aria-label="Live or test"
            value={mode ?? 'all'}
            onValueChange={(v) => onMode(v === 'all' ? undefined : v)}
            options={MODES}
          />
        )}
      </div>
      {open.length > 0 && <PendingCard items={open} events={shown.events} />}
      {entries.length > 0 ? (
        <TimelineStream entries={entries} timeline={shown} />
      ) : (
        <Card>
          <EmptyState
            compact
            icon={SearchX}
            title={mode === 'test' ? 'No test traffic' : 'No live traffic'}
            description="Switch to All to see everything for this key."
          />
        </Card>
      )}
    </div>
  )
}
